import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Save, FileCheck, ChevronUp, ChevronDown } from "lucide-react";

// Default starter checklists per app-type code — admin can tweak after creating.
const STARTERS = {
  SP: [
    { label: "Valid Passport (bio page)", mandatory: true },
    { label: "Letter of Acceptance (LOA / LOE)", mandatory: true },
    { label: "Provincial Attestation Letter (PAL/TAL)", mandatory: true },
    { label: "Proof of Funds / GIC", mandatory: true },
    { label: "Tuition Payment Receipt", mandatory: true },
    { label: "Previous Academic Transcripts", mandatory: true },
    { label: "IELTS / English test results", mandatory: true },
    { label: "Statement of Purpose", mandatory: true },
    { label: "Medical Examination (if applicable)", mandatory: false },
    { label: "Digital Photo", mandatory: true },
  ],
  SPE: [
    { label: "Current Passport", mandatory: true },
    { label: "Current Study Permit", mandatory: true },
    { label: "Enrollment / Attendance Letter", mandatory: true },
    { label: "Transcripts for current program", mandatory: true },
    { label: "Proof of Funds", mandatory: true },
    { label: "Digital Photo", mandatory: true },
  ],
  SOWP: [
    { label: "Spouse's Study/Work Permit", mandatory: true },
    { label: "Marriage Certificate", mandatory: true },
    { label: "Applicant's Passport", mandatory: true },
    { label: "Spouse's Enrollment / Employment Letter", mandatory: true },
    { label: "Joint Proof of Funds", mandatory: false },
    { label: "Digital Photo", mandatory: true },
  ],
  PGWP: [
    { label: "Passport", mandatory: true },
    { label: "Completion Letter from college", mandatory: true },
    { label: "Final Transcript", mandatory: true },
    { label: "Digital Photo", mandatory: true },
  ],
  VR: [
    { label: "Passport", mandatory: true },
    { label: "Current status document", mandatory: true },
    { label: "Reason for extension (letter)", mandatory: true },
    { label: "Proof of Funds", mandatory: true },
  ],
  SV: [
    { label: "Passport", mandatory: true },
    { label: "Invitation Letter from host (in Canada)", mandatory: true },
    { label: "Host's Notice of Assessment", mandatory: true },
    { label: "Medical Examination", mandatory: true },
    { label: "Visitor Insurance (min CA$100k)", mandatory: true },
    { label: "Proof of Funds", mandatory: true },
  ],
  LMIA: [
    { label: "Employer Business Documents", mandatory: true },
    { label: "Job Offer Letter", mandatory: true },
    { label: "Recruitment Proof (ads / resumes)", mandatory: true },
    { label: "Transition Plan (if High-wage)", mandatory: false },
    { label: "Applicant Resume", mandatory: true },
  ],
  PR: [
    { label: "Passport (all pages)", mandatory: true },
    { label: "ECA (Educational Credential Assessment)", mandatory: true },
    { label: "Language Test Results", mandatory: true },
    { label: "Police Certificates (all countries 6+ months)", mandatory: true },
    { label: "Medical Examination (upfront)", mandatory: true },
    { label: "Proof of Funds (CRS)", mandatory: true },
    { label: "Work Experience Letters", mandatory: true },
    { label: "Digital Photos", mandatory: true },
  ],
};

function newKey(label) {
  return (label || "item")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 40) || "item";
}

export default function ImmigrationChecklistTemplates() {
  const [types, setTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [items, setItems] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newMandatory, setNewMandatory] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [t, c] = await Promise.all([
      api.get("/immigration/app-types?include_inactive=false"),
      api.get("/immigration/checklist-templates"),
    ]);
    setTypes(t.data || []);
    setTemplates(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const selectedType = useMemo(() => types.find(t => t.id === selectedTypeId), [types, selectedTypeId]);
  const existingTemplate = useMemo(() => templates.find(x => x.app_type_id === selectedTypeId), [templates, selectedTypeId]);

  useEffect(() => {
    if (!selectedTypeId) { setItems([]); return; }
    if (existingTemplate) {
      setItems((existingTemplate.items || []).map(it => ({ ...it })));
    } else {
      const starter = STARTERS[(selectedType?.code || "").toUpperCase()] || [];
      setItems(starter.map(x => ({ ...x, key: newKey(x.label) })));
    }
  }, [selectedTypeId, existingTemplate, selectedType]);

  const addItem = () => {
    const label = newLabel.trim();
    if (!label) return toast.error("Label required");
    const key = newKey(label);
    if (items.some(i => i.key === key)) return toast.error("Duplicate item");
    setItems([...items, { key, label, mandatory: newMandatory }]);
    setNewLabel("");
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const toggleMandatory = (idx) => setItems(items.map((it, i) => i === idx ? { ...it, mandatory: !it.mandatory } : it));
  const updateLabel = (idx, label) => setItems(items.map((it, i) => i === idx ? { ...it, label, key: newKey(label) } : it));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };

  const save = async () => {
    if (!selectedTypeId) return toast.error("Pick an application type first");
    if (items.length === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      if (existingTemplate) {
        await api.patch(`/immigration/checklist-templates/${existingTemplate.id}`, { items });
      } else {
        await api.post(`/immigration/checklist-templates`, { app_type_id: selectedTypeId, items });
      }
      toast.success("Checklist saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const removeTemplate = async () => {
    if (!existingTemplate) return;
    if (!window.confirm("Delete this checklist template? Existing cases will fall back to an empty checklist.")) return;
    try {
      await api.delete(`/immigration/checklist-templates/${existingTemplate.id}`);
      toast.success("Template deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const typesWithStatus = useMemo(() => types.map(t => ({
    ...t, has_template: templates.some(x => x.app_type_id === t.id)
  })), [types, templates]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Checklist Templates</h1>
        <p className="text-sm text-slate-500 mt-1">
          Define the standard document checklist per application type. Each case picks up the template matching its type —
          officers can mark items received as they come in, and the full checklist can be downloaded as a branded PDF or
          emailed to the client in one click.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Left — types list */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 h-fit">
          <div className="text-xs uppercase text-slate-500 px-2 pb-2">Application Types</div>
          <div className="space-y-1">
            {typesWithStatus.length === 0 && <div className="text-sm text-slate-400 p-2">No active application types.</div>}
            {typesWithStatus.map(t => (
              <button
                key={t.id}
                onClick={() => setSelectedTypeId(t.id)}
                data-testid={`checklist-type-${t.code || t.id}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 ${
                  selectedTypeId === t.id ? "bg-orange-50 text-orange-800 border border-orange-200" : "hover:bg-slate-50 border border-transparent"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{t.name}</span>
                  <span className="mono text-[10px] text-slate-500">{t.code || "—"}</span>
                </div>
                {t.has_template ? (
                  <FileCheck className="w-4 h-4 text-emerald-600 shrink-0" title="Template configured"/>
                ) : (
                  <span className="text-[10px] text-slate-400 shrink-0">—</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right — editor */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          {!selectedTypeId ? (
            <div className="text-sm text-slate-500 py-10 text-center">
              Pick an application type on the left to start editing its checklist.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase text-slate-500">Editing</div>
                  <div className="heading-font text-xl font-semibold">{selectedType?.name}</div>
                  <div className="text-xs text-slate-500 mono mt-0.5">Code: {selectedType?.code || "—"} · {existingTemplate ? "Editing existing template" : "Creating new template"}</div>
                </div>
                <div className="flex gap-2">
                  {existingTemplate && (
                    <Button data-testid="checklist-delete-tpl" variant="outline" onClick={removeTemplate} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4 mr-1"/>Delete
                    </Button>
                  )}
                  <Button data-testid="checklist-save-tpl" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Save className="w-4 h-4 mr-1"/>{saving ? "Saving…" : (existingTemplate ? "Save Changes" : "Create Template")}
                  </Button>
                </div>
              </div>

              {/* Add row */}
              <div className="border border-dashed border-slate-300 rounded-lg p-3 flex items-end gap-3 bg-slate-50">
                <div className="flex-1 min-w-0">
                  <Label className="text-xs text-slate-600">Add document</Label>
                  <Input
                    data-testid="checklist-new-label"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addItem(); }}
                    placeholder="e.g. Letter of Acceptance (LOA)"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
                  <Checkbox checked={newMandatory} onCheckedChange={v => setNewMandatory(!!v)} data-testid="checklist-new-mandatory"/>
                  Mandatory
                </label>
                <Button onClick={addItem} data-testid="checklist-add-item" className="bg-slate-900 hover:bg-slate-700 text-white">
                  <Plus className="w-4 h-4 mr-1"/>Add
                </Button>
              </div>

              {/* Items list */}
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {items.length === 0 && (
                  <div className="p-4 text-sm text-slate-500">
                    No items yet. {selectedType?.code && STARTERS[selectedType.code] ? "A starter list is loaded above — review and Save." : "Add items above to get started."}
                  </div>
                )}
                {items.map((it, idx) => (
                  <div key={it.key + idx} className="flex items-center gap-3 p-3">
                    <div className="flex flex-col">
                      <button type="button" onClick={() => move(idx, -1)} className="text-slate-400 hover:text-slate-700"><ChevronUp className="w-3 h-3"/></button>
                      <button type="button" onClick={() => move(idx, +1)} className="text-slate-400 hover:text-slate-700"><ChevronDown className="w-3 h-3"/></button>
                    </div>
                    <div className="text-xs text-slate-400 w-6 text-right">{idx + 1}.</div>
                    <Input
                      value={it.label}
                      onChange={e => updateLabel(idx, e.target.value)}
                      className="flex-1"
                      data-testid={`checklist-item-label-${idx}`}
                    />
                    <label className="flex items-center gap-2 text-xs text-slate-600 w-[108px]">
                      <Checkbox checked={!!it.mandatory} onCheckedChange={() => toggleMandatory(idx)} data-testid={`checklist-item-mand-${idx}`}/>
                      Mandatory
                    </label>
                    <Button variant="ghost" size="icon" className="text-rose-600 hover:bg-rose-50" onClick={() => removeItem(idx)} data-testid={`checklist-remove-${idx}`}>
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-500">
                Tip — officers can tick items received inside each case. The PDF reflects the live status at the moment it's generated.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
